import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui';
import api from '../services/api';

const AgreementModal = ({ agreementType, onAccept, onCancel, isOpen }) => {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checkpoints, setCheckpoints] = useState({});
  const [error, setError] = useState('');
  const contentRef = useRef(null);

  const fetchAgreement = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/agreements/current/${agreementType}`);
      const data = response.data;

      if (data?.success) {
        setAgreement(data.data);
        // Initialize all checkpoints to false
        const initialCheckpoints = {};
        data.data.acknowledgementPoints.forEach(point => {
          initialCheckpoints[point.id] = false;
        });
        setCheckpoints(initialCheckpoints);
      } else {
        setError(data?.message || 'Failed to load agreement');
      }
    } catch (err) {
      console.error('Error fetching agreement:', err);

      try {
        const fallbackBaseUrl = process.env.REACT_APP_API_URL || 'https://api.mhdfmomentum.com/api';
        const fallbackResponse = await fetch(`${fallbackBaseUrl}/agreements/current/${agreementType}`);
        const contentType = fallbackResponse.headers.get('content-type') || '';

        if (!contentType.includes('application/json')) {
          throw new Error('Agreement endpoint returned non-JSON response');
        }

        const fallbackData = await fallbackResponse.json();
        if (fallbackData?.success) {
          setAgreement(fallbackData.data);
          const initialCheckpoints = {};
          fallbackData.data.acknowledgementPoints.forEach(point => {
            initialCheckpoints[point.id] = false;
          });
          setCheckpoints(initialCheckpoints);
        } else {
          setError(fallbackData?.message || 'Failed to load agreement');
        }
      } catch (fallbackError) {
        console.error('Agreement fallback fetch failed:', fallbackError);
        setError(err.response?.data?.message || 'Failed to load agreement. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [agreementType]);

  useEffect(() => {
    if (isOpen && agreementType) {
      setScrolledToBottom(false);
      setError('');
      fetchAgreement();
    }
  }, [isOpen, agreementType, fetchAgreement]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!agreement || !contentRef.current) return;

    const contentEl = contentRef.current;
    if (contentEl.scrollHeight <= contentEl.clientHeight + 5) {
      setScrolledToBottom(true);
    }
  }, [agreement]);

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    
    if (isAtBottom && !scrolledToBottom) {
      setScrolledToBottom(true);
    }
  };

  const handleCheckboxChange = (checkpointId) => {
    setCheckpoints(prev => ({
      ...prev,
      [checkpointId]: !prev[checkpointId]
    }));
  };

  const allChecked = () => {
    if (!agreement) return false;
    return agreement.acknowledgementPoints.every(point => checkpoints[point.id] === true);
  };

  const handleAccept = () => {
    if (!allChecked()) {
      setError('Please check all acknowledgement boxes before continuing.');
      return;
    }

    const checkpointResponses = agreement.acknowledgementPoints.map(point => ({
      checkpointId: point.id,
      acknowledged: checkpoints[point.id]
    }));

    onAccept({
      agreementVersionId: agreement.id,
      checkpointResponses
    });
  };

  const handleDownloadPDF = () => {
    if (agreement?.pdfUrl) {
      window.open(agreement.pdfUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-0 sm:p-4">
      <div className="bg-white shadow-xl w-full h-[100dvh] sm:h-auto sm:max-w-4xl sm:max-h-[90vh] flex flex-col rounded-none sm:rounded-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {agreementType === 'client'
                  ? 'Client'
                  : agreementType === 'clergy'
                    ? 'Clergy'
                    : agreementType === 'coach'
                      ? 'Coach'
                    : 'Clinician'} Agreement
              </h2>
              {agreement && (
                <p className="text-sm text-gray-500 mt-1">
                  Version {agreement.versionNumber} • Effective {new Date(agreement.effectiveDate).toLocaleDateString()}
                </p>
              )}
            </div>
            {agreement && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6" ref={contentRef} onScroll={handleScroll}>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {agreement && !loading && (
            <>
              {/* Agreement Text */}
              <div 
                className="prose max-w-none mb-8"
                dangerouslySetInnerHTML={{ __html: agreement.fullText }}
              />

              {/* Scroll Indicator */}
              {!scrolledToBottom && (
                <div className="sticky bottom-0 bg-gradient-to-t from-white to-transparent pt-8 pb-4 text-center">
                  <p className="text-sm text-gray-500 font-medium animate-bounce">
                    👇 Please scroll to the bottom to review acknowledgements
                  </p>
                </div>
              )}

              {/* Acknowledgement Checkpoints */}
              {scrolledToBottom && (
                <div className="mt-8 border-t border-gray-200 pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Acknowledgement
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Please read and check each statement below to confirm your understanding and acceptance:
                  </p>
                  
                  <div className="space-y-4">
                    {agreement.acknowledgementPoints.map((point) => (
                      <label
                        key={point.id}
                        className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={checkpoints[point.id] || false}
                          onChange={() => handleCheckboxChange(point.id)}
                          className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 leading-relaxed">
                          {point.text}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {error && scrolledToBottom && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              I Disagree
            </Button>
            
            <Button
              onClick={handleAccept}
              disabled={!scrolledToBottom || !allChecked() || loading}
              className="min-w-[200px]"
            >
              {!scrolledToBottom ? 'Scroll to Continue' : 
               !allChecked() ? 'Check All Items' : 
               'I Accept and Agree'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;
